import React, { useCallback, useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ArrowUpRight, Loader2 } from 'lucide-react';
import apiClient from '../lib/apiClient';
import { useLocale } from '../contexts/LocaleContext';
import { getAppLocale, interpolate } from '../content/appLocale';
import GenerationShell from '../components/GenerationShell';
import AccountNavigation from '../components/AccountNavigation';

const CREATE_CREDIT_CHECKOUT_FLAG = 'ih_create_credit_checkout';

const Billing = () => {
  const { locale } = useLocale();
  const copy = getAppLocale(locale);
  const { user, refreshUser } = useAuth();
  const freeCredits = user?.free_credits || 0;
  const paidCredits = user?.paid_credits || 0;
  const totalCredits = user?.total_credits ?? user?.credits ?? freeCredits + paidCredits;
  const navigate = useNavigate();
  const location = useLocation();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [packagesLoading, setPackagesLoading] = useState(true);
  const [packagesError, setPackagesError] = useState('');
  const [checkingPayment, setCheckingPayment] = useState(false);

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    setPackagesLoading(true);
    setPackagesError('');
    try {
      const { data } = await apiClient.get('/api/billing/packages');
      setPackages(data);
    } catch (error) {
      setPackagesError('Credit packs could not be loaded. Please try again.');
    } finally {
      setPackagesLoading(false);
    }
  };

  const pollPaymentStatus = useCallback(async (sessionId, attempts) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setCheckingPayment(false);
      alert(copy.billing.paymentTimeout);
      return;
    }

    try {
      const { data } = await apiClient.get(`/api/billing/checkout/status/${sessionId}`);

      if (data.payment_status === 'paid') {
        setCheckingPayment(false);
        await refreshUser();
        const shouldRedirectDashboard =
          typeof window !== 'undefined' && window.localStorage.getItem(CREATE_CREDIT_CHECKOUT_FLAG) === '1';
        if (typeof window !== 'undefined') {
          window.localStorage.removeItem(CREATE_CREDIT_CHECKOUT_FLAG);
        }
        alert(interpolate(copy.billing.paymentSuccess, { credits: data.credits }));
        navigate(shouldRedirectDashboard ? '/dashboard' : '/billing', { replace: true });
        return;
      } else if (data.status === 'expired') {
        setCheckingPayment(false);
        if (typeof window !== 'undefined') {
          window.localStorage.removeItem(CREATE_CREDIT_CHECKOUT_FLAG);
        }
        alert(copy.billing.paymentExpired);
        return;
      }

      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setCheckingPayment(false);
    }
  }, [copy.billing.paymentExpired, copy.billing.paymentSuccess, copy.billing.paymentTimeout, navigate, refreshUser]);

  const checkPaymentSuccess = useCallback(async () => {
    const params = new URLSearchParams(location.search);
    const sessionId = params.get('session_id');
    
    if (sessionId) {
      setCheckingPayment(true);
      await pollPaymentStatus(sessionId, 0);
      return;
    }
    if (typeof window !== 'undefined' && location.pathname === '/billing') {
      window.localStorage.removeItem(CREATE_CREDIT_CHECKOUT_FLAG);
    }
  }, [location.pathname, location.search, pollPaymentStatus]);

  useEffect(() => {
    checkPaymentSuccess();
  }, [checkPaymentSuccess]);

  const handlePurchase = async (packageId) => {
    console.log('Purchase button clicked for package:', packageId);
    setLoading(true);
    try {
      const originUrl = window.location.origin;
      console.log('Creating checkout session with origin:', originUrl);
      const { data } = await apiClient.post('/api/billing/checkout', {
        package_id: packageId,
        origin_url: originUrl
      });
      console.log('Checkout session created:', data);
      if (data.url) {
        console.log('Redirecting to Stripe:', data.url);
        window.location.href = data.url;
      } else {
        throw new Error(copy.billing.noCheckoutUrl);
      }
    } catch (error) {
      console.error('Checkout failed:', error);
      alert(error.response?.data?.detail || interpolate(copy.billing.checkoutFailed, { error: error.message }));
      setLoading(false);
    }
  };

  return (
    <GenerationShell>
      <div className="studio-account-page studio-billing-page">
        <div className="studio-page-heading"><div><h1>{copy.profileMenu.creditsAndPlans}</h1><p>{copy.billing.heroSubtext}</p></div></div>
        <AccountNavigation />
        <section className="studio-credit-summary" aria-labelledby="credit-balance-heading">
          <div><h2 id="credit-balance-heading">{copy.profileMenu.balanceHeading}</h2><p className="studio-credit-total">{totalCredits} <span>credits</span></p></div>
          <dl><div><dt>Free credits</dt><dd>{freeCredits}</dd></div><div><dt>Paid credits</dt><dd>{paidCredits}</dd></div></dl>
        </section>
        {checkingPayment && <div role="status" data-testid="payment-checking-status" className="studio-payment-status"><Loader2 size={20} className="studio-spinner" aria-hidden />{copy.billing.checkingPayment}</div>}
        <section className="studio-credit-packs" aria-labelledby="credit-packs-heading">
          <div className="studio-billing-section-heading"><h2 id="credit-packs-heading">Add credits</h2><div>{(copy.billing.pricingExplainerLines || []).map(line => <p key={line}>{line}</p>)}</div></div>
          {packagesLoading && <p role="status" className="studio-payment-status">Loading credit packs...</p>}
          {packagesError && <div role="alert" className="studio-alert studio-alert-error">{packagesError} <button className="studio-inline-link" onClick={fetchPackages}>Try again</button></div>}
          {!packagesLoading && !packagesError && packages.length === 0 && <p className="studio-payment-status">No credit packs are available right now. <Link className="studio-inline-link" to="/help">Contact support</Link></p>}
          <div data-testid="billing-packages" className="studio-pack-list">
            {packages.map(pkg => <article key={pkg.id} data-testid={`package-${pkg.id}`} className="studio-pack-row">
              <div><h3>{pkg.name}</h3><p>{pkg.credits} credits</p></div>
              <div className="studio-pack-description">{copy.billing.packDetails?.[pkg.id] && <><p>{copy.billing.packDetails[pkg.id].tagline}</p><small>{copy.billing.packDetails[pkg.id].approxOutput}</small></>}</div>
              <p className="studio-pack-price">${pkg.price}</p>
              <button data-testid={`buy-${pkg.id}-button`} onClick={() => handlePurchase(pkg.id)} disabled={loading || checkingPayment} className="studio-button studio-button-primary">{loading ? copy.billing.processing : copy.billing.purchase}<ArrowUpRight size={16} aria-hidden /></button>
            </article>)}
          </div>
        </section>
        <section className="studio-billing-details"><div><h2>{copy.billing.howCreditsWork}</h2><ul>{(copy.billing.howCreditsBullets || []).map(line => <li key={line}>{line}</li>)}</ul></div>
          <div><h2>{copy.billing.commercialUse}</h2><p>{copy.billing.packBulletFirst}</p><p>{copy.billing.allStylePresets}</p></div>
        </section>
        <footer className="studio-account-footer"><p>{copy.billing.paymentDisclaimerBeforeRefund}<Link to="/refund-policy">{copy.billing.refundPolicyLink}</Link>{copy.billing.paymentDisclaimerBetween}<Link to="/terms">{copy.billing.termsOfServiceLink}</Link>{copy.billing.paymentDisclaimerAfter}</p></footer>
      </div>
    </GenerationShell>
  );
};

export default Billing;
